import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode
} from 'react';
import { ArrowRight, CheckCircle2 } from 'lucide-react';
import { Link } from 'react-router-dom';

import { Badge } from '@/shared/components/ui/Badge';
import { useI18n } from '@/shared/contexts/I18nProvider';

import {
  platformCapabilityMeta,
  type PlatformCapability,
  type PlatformCapabilityVariant
} from '../HomeData';
import { TypicalPathAnimation } from './TypicalPathAnimation';

const initialSectionId = 'platform-flow';
const platformSnapOffset = 144;
const platformSnapMediaQuery = '(min-width: 1024px)';
const platformSnapType = 'y proximity';
const platformProgrammaticScrollLockMs = 1400;

const classNames = (...classes: Array<string | false | null | undefined>) =>
  classes.filter(Boolean).join(' ');

const applyPlatformSnapState = (isEnabled: boolean) => {
  const shouldEnableSnap =
    isEnabled && window.matchMedia(platformSnapMediaQuery).matches;
  const snapType = shouldEnableSnap ? platformSnapType : 'none';
  const snapOffset = `${platformSnapOffset}px`;
  const root = document.documentElement;
  const body = document.body;

  root.style.scrollSnapType = snapType;
  root.style.scrollPaddingTop = snapOffset;
  body.style.scrollSnapType = snapType;
  body.style.scrollPaddingTop = snapOffset;
};

type PlatformTone = {
  bg: string;
  line: string;
  text: string;
};

const platformAccentTone: PlatformTone = {
  bg: 'bg-accent-primary/10',
  line: 'bg-accent-primary',
  text: 'text-accent-primary'
};

const platformTones: Record<PlatformCapabilityVariant, PlatformTone> = {
  standard: platformAccentTone,
  split: platformAccentTone,
  highlight: platformAccentTone,
  metrics: platformAccentTone,
  pipeline: platformAccentTone,
  timeline: platformAccentTone,
  compact: platformAccentTone
};

type PlatformSectionMode = 'flow' | 'serving' | 'intelligence';

type PlatformSection = {
  capabilities: PlatformCapability[];
  icon: PlatformCapability['icon'];
  id: string;
  label: string;
  mode: PlatformSectionMode;
  step: string;
  shortLabel: string;
  summary: string;
  title: string;
  tone: PlatformTone;
};

type PlatformSectionBlueprint = {
  id: string;
  indexes: readonly number[];
  mode: PlatformSectionMode;
  shortLabel: string;
};

const platformSectionBlueprints: readonly PlatformSectionBlueprint[] = [
  {
    id: 'platform-flow',
    indexes: [0, 1],
    mode: 'flow',
    shortLabel: 'Data Flow'
  },
  {
    id: 'platform-serving',
    indexes: [2, 3, 4],
    mode: 'serving',
    shortLabel: 'Data Serving'
  },
  {
    id: 'platform-intelligence',
    indexes: [5, 6],
    mode: 'intelligence',
    shortLabel: 'AI Operations'
  }
];

type PlatformBackgroundFlowRoute = {
  key: string;
  path: string;
  duration: number;
  delay: number;
};

const platformBackgroundPulseOffsets = [0, 0.36, 0.72] as const;

const platformDesktopBackgroundFlowRoutes: PlatformBackgroundFlowRoute[] = [
  {
    key: 'center',
    path: 'M600 0 C592 300 612 530 600 820 C588 1130 616 1360 600 1690 C586 2010 610 2250 600 2600',
    duration: 16.8,
    delay: 0
  }
];

const platformMobileBackgroundFlowRoutes: PlatformBackgroundFlowRoute[] = [
  {
    key: 'center',
    path: 'M195 0 C190 275 202 500 195 760 C188 1040 204 1290 195 1580 C186 1880 202 2160 195 2450',
    duration: 16.2,
    delay: 0
  }
];

const renderPlatformBackgroundFlow = (
  routes: ReadonlyArray<PlatformBackgroundFlowRoute>
) => (
  <>
    {routes.map(route => (
      <path
        key={route.key}
        d={route.path}
        fill="none"
        stroke="currentColor"
        strokeWidth="1.2"
        strokeOpacity="0.2"
        vectorEffect="non-scaling-stroke"
      />
    ))}

    {routes.flatMap(route =>
      platformBackgroundPulseOffsets.map((offset, index) => {
        const begin = route.delay + route.duration * offset;

        return (
          <g
            key={`${route.key}-${offset}`}
            className="platform-background-flow-particle"
            opacity="0"
          >
            <circle
              r={index === 1 ? '6.5' : '5'}
              fill="currentColor"
              opacity="0.12"
            />
            <circle r={index === 1 ? '2.8' : '2.2'} fill="currentColor" />
            <animateMotion
              path={route.path}
              dur={`${route.duration}s`}
              begin={`${begin}s`}
              repeatCount="indefinite"
            />
            <animate
              attributeName="opacity"
              values="0;1;1;0"
              keyTimes="0;0.08;0.86;1"
              dur={`${route.duration}s`}
              begin={`${begin}s`}
              repeatCount="indefinite"
            />
          </g>
        );
      })
    )}
  </>
);

const PlatformBackgroundFlow = () => (
  <div
    aria-hidden="true"
    className="pointer-events-none absolute inset-0 z-0 overflow-hidden"
  >
    <svg
      viewBox="0 0 1200 2600"
      className="platform-background-flow absolute left-1/2 top-0 hidden h-full w-[1200px] max-w-[calc(100vw-2rem)] -translate-x-1/2 text-accent-primary sm:block"
      preserveAspectRatio="xMidYMin slice"
    >
      {renderPlatformBackgroundFlow(platformDesktopBackgroundFlowRoutes)}
    </svg>

    <svg
      viewBox="0 0 390 2450"
      className="platform-background-flow absolute left-1/2 top-0 h-full w-full max-w-[560px] -translate-x-1/2 text-accent-primary sm:hidden"
      preserveAspectRatio="xMidYMin slice"
    >
      {renderPlatformBackgroundFlow(platformMobileBackgroundFlowRoutes)}
    </svg>
  </div>
);

const RouteChip = ({ item, tone }: { item: string; tone: PlatformTone }) => (
  <span
    className={classNames(
      'inline-flex rounded-full px-3 py-1 font-mono text-xs',
      tone.bg,
      tone.text
    )}
  >
    {item}
  </span>
);

const CapabilityCard = ({
  capability,
  compact = false
}: {
  capability: PlatformCapability;
  compact?: boolean;
}) => {
  const { content } = useI18n();
  const CapabilityIcon = capability.icon;
  const tone = platformTones[capability.variant];
  const visibleOutcomes = compact
    ? capability.outcomes.slice(0, 1)
    : capability.outcomes;
  const visibleRoute = compact
    ? capability.route.slice(0, 3)
    : capability.route;

  return (
    <article
      className={classNames(
        'rounded-lg bg-background-card/70',
        compact ? 'p-3' : 'p-4'
      )}
    >
      <div className="flex items-start gap-3">
        <span
          className={classNames(
            'flex shrink-0 items-center justify-center rounded-lg',
            compact ? 'h-9 w-9' : 'h-11 w-11',
            tone.bg,
            tone.text
          )}
        >
          <CapabilityIcon className={compact ? 'h-4 w-4' : 'h-5 w-5'} />
        </span>

        <div className="min-w-0">
          <div className={classNames('font-mono text-xs', tone.text)}>
            {capability.step} / {content.home.platform.layerLabel}
          </div>
          <h4
            className={classNames(
              'mt-2 font-light leading-tight text-text-primary',
              compact ? 'text-base' : 'text-xl'
            )}
          >
            {capability.title}
          </h4>
        </div>
      </div>

      <p
        className={classNames(
          'text-sm leading-6 text-text-primary',
          compact ? 'mt-3' : 'mt-5'
        )}
      >
        {capability.lead}
      </p>

      {!compact && (
        <p className="mt-4 text-sm leading-6 text-text-secondary">
          {capability.description}
        </p>
      )}

      <div
        className={classNames(
          'flex flex-wrap gap-2',
          compact ? 'mt-4' : 'mt-5'
        )}
      >
        {visibleRoute.map(routeItem => (
          <RouteChip key={routeItem} item={routeItem} tone={tone} />
        ))}
      </div>

      <div className={classNames('grid gap-2', compact ? 'mt-4' : 'mt-5')}>
        {visibleOutcomes.map(outcome => (
          <div key={outcome} className="flex gap-3 text-sm text-text-secondary">
            <CheckCircle2
              className={classNames('mt-0.5 h-4 w-4 shrink-0', tone.text)}
            />
            <span className="leading-6">{outcome}</span>
          </div>
        ))}
      </div>
    </article>
  );
};

const SectionIntro = ({
  section,
  isActive
}: {
  section: PlatformSection;
  isActive: boolean;
}) => {
  const { content } = useI18n();
  const SectionIcon = section.icon;

  return (
    <div>
      <div
        className={classNames(
          'flex h-12 w-12 items-center justify-center rounded-lg transition-colors duration-300',
          isActive
            ? classNames(section.tone.bg, section.tone.text)
            : 'bg-background-card/70 text-text-muted'
        )}
      >
        <SectionIcon className="h-6 w-6" />
      </div>

      <div
        className={classNames(
          'mt-5 font-mono text-xs uppercase tracking-[0.2em]',
          section.tone.text
        )}
      >
        {section.step} / {content.home.platform.layerLabel}
      </div>

      <h3
        id={`${section.id}-title`}
        className="mt-3 text-2xl font-light leading-tight sm:text-3xl"
      >
        {section.title}
      </h3>

      <p className="mt-0 max-w-xl text-base leading-7 text-text-secondary">
        {section.capabilities.map(capability => capability.lead).join(' ')}
      </p>

      <div className="mt-5 flex flex-wrap gap-2">
        {section.capabilities.map(capability => (
          <RouteChip
            key={capability.id}
            item={capability.label}
            tone={platformTones[capability.variant]}
          />
        ))}
      </div>
    </div>
  );
};

const FlowVisual = ({ section }: { section: PlatformSection }) => {
  const { content } = useI18n();

  return (
    <TypicalPathAnimation
      capabilities={section.capabilities}
      eyebrow={content.home.platform.typicalPath}
    />
  );
};

const ServingVisual = ({ section }: { section: PlatformSection }) => {
  const { content } = useI18n();

  return (
    <div className="grid gap-4">
      <div className="rounded-lg bg-background-card/70 p-4">
        <div className="font-mono text-xs uppercase tracking-[0.2em] text-text-muted">
          {content.home.platform.typicalPath}
        </div>

        <div className="mt-5 grid gap-3 lg:grid-cols-3">
          {section.capabilities.map((capability, index) => {
            const tone = platformTones[capability.variant];

            return (
              <div
                key={capability.id}
                className={classNames(
                  'flex min-h-52 flex-col rounded-lg bg-background-secondary p-3'
                )}
                style={{ transform: `translateY(${index * 0.55}rem)` }}
              >
                <div className={classNames('font-mono text-xs', tone.text)}>
                  {capability.step}
                </div>
                <div className="mt-3 text-lg text-text-primary">
                  {capability.title}
                </div>

                <div className="mt-4 grid gap-2">
                  {capability.route.map(routeItem => (
                    <div
                      key={routeItem}
                      className="rounded-md bg-background-card px-3 py-2 font-mono text-xs text-text-secondary"
                    >
                      {routeItem}
                    </div>
                  ))}
                </div>

                <div className="mt-auto pt-4">
                  <div
                    className={classNames('h-1.5 rounded-full', tone.line)}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <div className="grid gap-3 sm:grid-cols-3">
        {section.capabilities.flatMap(capability =>
          capability.outcomes.slice(0, 1).map(outcome => (
            <div
              key={`${capability.id}-${outcome}`}
              className="rounded-lg bg-background-card/70 p-3 text-sm leading-6 text-text-secondary"
            >
              {outcome}
            </div>
          ))
        )}
      </div>
    </div>
  );
};

const IntelligenceVisual = ({ section }: { section: PlatformSection }) => {
  const { content } = useI18n();
  const steps = section.capabilities.flatMap(capability =>
    capability.route.map(routeItem => ({
      capability,
      routeItem
    }))
  );

  return (
    <div className="rounded-lg bg-background-card/70 p-4">
      <div className="font-mono text-xs uppercase tracking-[0.2em] text-text-muted">
        {content.home.platform.typicalPath}
      </div>

      <div className="mt-5 grid gap-2">
        {steps.map(({ capability, routeItem }, index) => {
          const tone = platformTones[capability.variant];

          return (
            <div
              key={`${capability.id}-${routeItem}`}
              className="grid gap-3 rounded-lg bg-background-secondary p-3 sm:grid-cols-[auto_1fr_auto] sm:items-center"
            >
              <span className={classNames('font-mono text-xs', tone.text)}>
                0{index + 1}
              </span>
              <span className="text-text-primary">{routeItem}</span>
              {index < steps.length - 1 && (
                <ArrowRight
                  aria-hidden="true"
                  className={classNames('hidden h-4 w-4 sm:block', tone.text)}
                />
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

const SectionVisual = ({ section }: { section: PlatformSection }) => {
  switch (section.mode) {
    case 'serving':
      return <ServingVisual section={section} />;
    case 'intelligence':
      return <IntelligenceVisual section={section} />;
    case 'flow':
      return <FlowVisual section={section} />;
    default:
      return <FlowVisual section={section} />;
  }
};

const SnapSection = ({
  children,
  index,
  isActive,
  section,
  sectionRef
}: {
  children: ReactNode;
  index: number;
  isActive: boolean;
  section: PlatformSection;
  sectionRef: (node: HTMLElement | null) => void;
}) => (
  <article
    id={section.id}
    ref={sectionRef}
    aria-labelledby={`${section.id}-title`}
    className="relative min-h-[720px] px-4 pb-4 pt-0 sm:px-5 sm:pb-5 sm:pt-0 lg:sticky lg:top-36 lg:h-[calc(100vh-9rem)] lg:min-h-[620px] lg:snap-start lg:snap-always lg:p-6"
    style={{ zIndex: 20 + index }}
  >
    <div className="relative mx-auto h-full max-w-7xl overflow-hidden border border-border-primary/80 bg-background-secondary/95 p-4 shadow-2xl shadow-shadow backdrop-blur-xl transition-colors duration-300 sm:p-5 lg:border-0 lg:p-6">
      <div
        aria-hidden="true"
        className={classNames(
          'absolute inset-x-0 top-0 h-1 transition-colors duration-300',
          isActive ? section.tone.line : 'bg-border-primary'
        )}
      />

      {children}
    </div>
  </article>
);

const buildPlatformSections = (
  capabilities: PlatformCapability[]
): PlatformSection[] =>
  platformSectionBlueprints.flatMap((blueprint, index) => {
    const sectionCapabilities = blueprint.indexes
      .map(capabilityIndex => capabilities[capabilityIndex])
      .filter(
        (capability): capability is PlatformCapability =>
          capability !== undefined
      );
    const firstCapability = sectionCapabilities[0];

    if (firstCapability === undefined) {
      return [];
    }

    return [
      {
        capabilities: sectionCapabilities,
        icon: firstCapability.icon,
        id: blueprint.id,
        label: sectionCapabilities
          .map(capability => capability.label)
          .join(' / '),
        mode: blueprint.mode,
        shortLabel: blueprint.shortLabel,
        step: `0${index + 1}`,
        summary: sectionCapabilities
          .map(capability => capability.lead)
          .join(' '),
        title: sectionCapabilities
          .map(capability => capability.title)
          .join(' + '),
        tone: platformTones[firstCapability.variant]
      }
    ];
  });

export const Platform = () => {
  const { content, localizedPath } = useI18n();
  const capabilities = useMemo<PlatformCapability[]>(
    () =>
      platformCapabilityMeta.map((capabilityMeta, index) => ({
        ...capabilityMeta,
        ...content.home.platform.capabilities[index]
      })),
    [content.home.platform.capabilities]
  );
  const platformSections = useMemo(
    () => buildPlatformSections(capabilities),
    [capabilities]
  );
  const [activeSectionId, setActiveSectionId] = useState(initialSectionId);

  const sectionStack = useRef<HTMLDivElement | null>(null);
  const sectionNodes = useRef<Record<string, HTMLElement | null>>({});
  const menuButtons = useRef<Record<string, HTMLButtonElement | null>>({});
  const menuNav = useRef<HTMLElement | null>(null);
  const isTicking = useRef(false);
  const shouldSmoothMenuScroll = useRef(false);
  const snapRestoreTimer = useRef<number | null>(null);
  const platformMenu = useRef<HTMLDivElement | null>(null);
  const programmaticScroll = useRef<{
    releaseAt: number;
    targetId: string;
    targetTop: number;
  } | null>(null);

  const getSections = useCallback(() => {
    return platformSectionBlueprints
      .map(section => sectionNodes.current[section.id])
      .filter((section): section is HTMLElement => Boolean(section));
  }, []);

  const getSectionLayoutTop = useCallback((id: string) => {
    const stack = sectionStack.current;

    if (!stack) {
      return null;
    }

    let top = stack.getBoundingClientRect().top + window.scrollY;

    for (const section of platformSectionBlueprints) {
      if (section.id === id) {
        return top;
      }

      const sectionNode = sectionNodes.current[section.id];

      if (!sectionNode) {
        return null;
      }

      top += sectionNode.offsetHeight;
    }

    return null;
  }, []);

  const getPlatformSectionScrollOffset = useCallback(() => {
    if (window.matchMedia(platformSnapMediaQuery).matches) {
      return platformSnapOffset;
    }

    const menu = platformMenu.current;

    if (!menu) {
      return platformSnapOffset;
    }

    const stickyTop = Number.parseFloat(window.getComputedStyle(menu).top);
    const menuHeight = menu.getBoundingClientRect().height;
    const breathingRoom = 16;

    return Math.ceil(
      (Number.isFinite(stickyTop) ? stickyTop : platformSnapOffset) +
        menuHeight +
        breathingRoom
    );
  }, []);

  const restorePlatformSnap = useCallback(() => {
    if (snapRestoreTimer.current !== null) {
      window.clearTimeout(snapRestoreTimer.current);
      snapRestoreTimer.current = null;
    }

    applyPlatformSnapState(true);
  }, []);

  const updateActiveSection = useCallback(() => {
    const lockedScroll = programmaticScroll.current;

    if (lockedScroll) {
      const isAtTarget = Math.abs(window.scrollY - lockedScroll.targetTop) < 4;
      const isExpired = window.performance.now() > lockedScroll.releaseAt;

      if (!isAtTarget && !isExpired) {
        setActiveSectionId(current =>
          current === lockedScroll.targetId ? current : lockedScroll.targetId
        );
        return;
      }

      programmaticScroll.current = null;
      restorePlatformSnap();
    }

    const sections = getSections();

    if (sections.length === 0) {
      return;
    }

    const activeLine = window.scrollY + window.innerHeight * 0.46;
    const passedSections = sections.filter(section => {
      const sectionTop = getSectionLayoutTop(section.id);

      return sectionTop !== null && sectionTop <= activeLine;
    });
    const activeSection =
      passedSections[passedSections.length - 1] ?? sections[0];

    setActiveSectionId(current =>
      current === activeSection.id ? current : activeSection.id
    );
  }, [getSectionLayoutTop, getSections, restorePlatformSnap]);

  useEffect(() => {
    const root = document.documentElement;
    const body = document.body;
    const previousScrollSnapType = root.style.scrollSnapType;
    const previousScrollPaddingTop = root.style.scrollPaddingTop;
    const previousBodyScrollSnapType = body.style.scrollSnapType;
    const previousBodyScrollPaddingTop = body.style.scrollPaddingTop;

    applyPlatformSnapState(true);
    window.addEventListener('resize', restorePlatformSnap);

    return () => {
      if (snapRestoreTimer.current !== null) {
        window.clearTimeout(snapRestoreTimer.current);
        snapRestoreTimer.current = null;
      }

      window.removeEventListener('resize', restorePlatformSnap);
      root.style.scrollSnapType = previousScrollSnapType;
      root.style.scrollPaddingTop = previousScrollPaddingTop;
      body.style.scrollSnapType = previousBodyScrollSnapType;
      body.style.scrollPaddingTop = previousBodyScrollPaddingTop;
    };
  }, [restorePlatformSnap]);

  useEffect(() => {
    const handleScroll = () => {
      if (isTicking.current) {
        return;
      }

      isTicking.current = true;

      window.requestAnimationFrame(() => {
        updateActiveSection();
        isTicking.current = false;
      });
    };

    updateActiveSection();

    const initialUpdate = window.requestAnimationFrame(updateActiveSection);

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleScroll);

    return () => {
      window.cancelAnimationFrame(initialUpdate);
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleScroll);
    };
  }, [updateActiveSection]);

  useEffect(() => {
    const activeMenuButton = menuButtons.current[activeSectionId];
    const nav = menuNav.current;

    if (!activeMenuButton || !nav) {
      return;
    }

    const navRect = nav.getBoundingClientRect();
    const buttonRect = activeMenuButton.getBoundingClientRect();
    const centeredLeft =
      nav.scrollLeft +
      buttonRect.left -
      navRect.left -
      (navRect.width - buttonRect.width) / 2;
    const nextScrollLeft = Math.max(centeredLeft, 0);
    const scrollBehavior: ScrollBehavior = shouldSmoothMenuScroll.current
      ? 'smooth'
      : 'auto';

    shouldSmoothMenuScroll.current = false;

    if (Math.abs(nav.scrollLeft - nextScrollLeft) < 1) {
      return;
    }

    nav.scrollTo({
      left: nextScrollLeft,
      behavior: scrollBehavior
    });
  }, [activeSectionId]);

  const scrollToPlatformSection = (id: string) => {
    const target = sectionNodes.current[id];

    if (!target) {
      return;
    }

    const targetLayoutTop = getSectionLayoutTop(id);

    if (targetLayoutTop === null) {
      return;
    }

    const targetTop = Math.max(
      targetLayoutTop - getPlatformSectionScrollOffset(),
      0
    );
    shouldSmoothMenuScroll.current = true;

    programmaticScroll.current = {
      releaseAt: window.performance.now() + platformProgrammaticScrollLockMs,
      targetId: id,
      targetTop
    };

    applyPlatformSnapState(false);
    setActiveSectionId(id);

    window.scrollTo({
      top: targetTop,
      behavior: 'smooth'
    });

    if (snapRestoreTimer.current !== null) {
      window.clearTimeout(snapRestoreTimer.current);
    }

    snapRestoreTimer.current = window.setTimeout(() => {
      snapRestoreTimer.current = null;
      applyPlatformSnapState(true);
      updateActiveSection();
    }, platformProgrammaticScrollLockMs);
  };

  return (
    <>
      <div id="platform" data-scroll-offset="160" aria-hidden="true" />
      <section className="relative isolate overflow-visible bg-background-secondary py-20">
        <PlatformBackgroundFlow />

        <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[0.88fr_1.12fr] lg:items-end">
            <div>
              <Badge variant="secondary" className="mb-4">
                {content.home.platform.badge}
              </Badge>
              <h2 className="text-4xl font-light leading-tight sm:text-5xl">
                {content.home.platform.title}
              </h2>
            </div>

            <p className="text-lg leading-8 text-text-secondary">
              {content.home.platform.description}
            </p>
          </div>
        </div>

        <div
          ref={platformMenu}
          className="pointer-events-none sticky top-[calc(var(--app-navbar-height,5.75rem)+0.5rem)] z-30 mb-3 px-5 sm:px-6 lg:top-24 lg:my-10 lg:px-8"
        >
          <div className="pointer-events-auto mx-auto flex w-full max-w-[calc(100vw-2.5rem)] items-center gap-3 rounded-lg border border-border-primary/70 bg-background-card/80 px-2 py-2 shadow-lg shadow-shadow backdrop-blur-xl transition-colors sm:w-fit sm:max-w-full sm:px-3">
            <nav
              ref={menuNav}
              aria-label={content.home.platform.navAriaLabel}
              className="flex min-w-0 flex-1 items-center justify-center gap-5 overflow-x-auto sm:gap-6"
            >
              {platformSections.map(section => {
                const isActive = activeSectionId === section.id;

                return (
                  <button
                    key={section.id}
                    ref={node => {
                      menuButtons.current[section.id] = node;
                    }}
                    type="button"
                    aria-controls={section.id}
                    aria-current={isActive ? 'step' : undefined}
                    aria-label={`${content.home.platform.scrollToPrefix} ${section.title}`}
                    onClick={() => scrollToPlatformSection(section.id)}
                    className={classNames(
                      'relative flex h-9 shrink-0 cursor-pointer items-center justify-center px-1 text-center text-sm transition-colors duration-300',
                      isActive
                        ? 'text-text-primary'
                        : 'text-text-muted hover:text-text-primary'
                    )}
                  >
                    {section.shortLabel}
                    <span
                      aria-hidden="true"
                      className={classNames(
                        'absolute inset-x-0 bottom-0 h-px transition-colors duration-300',
                        isActive ? section.tone.line : 'bg-transparent'
                      )}
                    />
                  </button>
                );
              })}
            </nav>

            <Link
              to={localizedPath('/contact')}
              className="hidden h-9 shrink-0 items-center rounded-md bg-accent-primary px-4 text-sm font-medium text-text-inverse transition-colors hover:bg-button-primary-hover sm:inline-flex"
            >
              {content.nav.contact}
            </Link>
          </div>
        </div>

        <div className="relative z-10 w-full pt-0 lg:pt-8">
          <div
            ref={sectionStack}
            className="relative w-full bg-background-primary/45"
          >
            {platformSections.map((section, index) => {
              const isActive = activeSectionId === section.id;

              return (
                <SnapSection
                  key={section.id}
                  index={index}
                  isActive={isActive}
                  section={section}
                  sectionRef={node => {
                    sectionNodes.current[section.id] = node;
                  }}
                >
                  <div className="grid gap-8 lg:grid-cols-[0.42fr_0.58fr] lg:items-start">
                    <SectionIntro section={section} isActive={isActive} />
                    <SectionVisual section={section} />
                  </div>

                  <div
                    className={classNames(
                      'mt-5 grid gap-3',
                      section.capabilities.length > 2
                        ? 'lg:grid-cols-3'
                        : 'lg:grid-cols-2'
                    )}
                  >
                    {section.capabilities.map(capability => (
                      <CapabilityCard
                        key={capability.id}
                        capability={capability}
                        compact
                      />
                    ))}
                  </div>
                </SnapSection>
              );
            })}
          </div>
        </div>
      </section>
    </>
  );
};
